import React, { useState } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

/*
 * Interactive end-to-end tutorial.
 * The user picks a role (parent or sitter) and is walked through the whole
 * lifecycle with mock-UI demos styled to match the REAL app screens
 * (JobList cards, PaymentModal, work log modal, status pills, etc.).
 * Steps whose demo has a primary action require that click before "Next" unlocks.
 */

/* ---------- shared demo chrome ---------- */

const DemoShell = ({ children, label }) => (
  <div className="mt-5 rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 p-3 sm:p-4">
    <p className="text-[11px] font-bold uppercase tracking-wider text-sky-500 mb-3">
      {label || 'Try it — this is a demo'}
    </p>
    {children}
  </div>
);

const DemoSuccess = ({ children }) => (
  <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm font-medium text-emerald-800">
    <CheckCircleIcon className="w-5 h-5 flex-shrink-0 text-emerald-500" />
    <span>{children}</span>
  </div>
);

// Status pill + pulse dot, exactly like the job/application cards in the app
const StatusHeader = ({ dot, pill, pillClasses, right }) => (
  <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 flex items-center justify-between flex-wrap gap-y-1">
    <div className="flex items-center space-x-3">
      <div className={`w-3 h-3 rounded-full ${dot}`}></div>
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${pillClasses}`}>{pill}</span>
    </div>
    {right && <div className="text-xs text-slate-500">{right}</div>}
  </div>
);

const AppCard = ({ children }) => (
  <div className="bg-white/80 backdrop-blur-sm border border-white/40 rounded-2xl shadow-lg overflow-hidden">{children}</div>
);

// The app's primary button styles, verbatim
const btn = {
  blue: 'px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg shadow-blue-500/25',
  green: 'px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg shadow-green-500/25',
  red: 'px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-red-500/25',
  amber: 'px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-lg shadow-amber-500/25',
  stripe: 'w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg',
};

const TimeChip = ({ children, bad, good }) => (
  <span className={`px-2.5 py-1.5 border rounded-lg font-medium text-xs bg-white whitespace-nowrap ${
    bad ? 'border-red-300 text-red-600' : good ? 'border-emerald-300 text-emerald-700' : 'border-slate-300 text-slate-800'
  }`}>{children}</span>
);

const ChatDemo = ({ fromOther, fromMe }) => (
  <AppCard>
    <div className="p-4 space-y-2">
      <div className="flex">
        <div className="max-w-[75%] bg-gray-100 text-gray-800 text-sm rounded-2xl rounded-bl-sm px-4 py-2">{fromOther}</div>
      </div>
      <div className="flex justify-end">
        <div className="max-w-[75%] bg-blue-500 text-white text-sm rounded-2xl rounded-br-sm px-4 py-2">{fromMe}</div>
      </div>
      <div className="flex gap-2 pt-2">
        <div className="flex-1 px-4 py-2 border border-slate-300 rounded-xl text-sm text-gray-400">Type a message…</div>
        <span className="px-4 py-2 bg-blue-500 text-white text-sm font-semibold rounded-xl">Send</span>
      </div>
    </div>
  </AppCard>
);

const SignupDemo = ({ role }) => (
  <div className="bg-white shadow-xl rounded-xl p-5 max-w-sm mx-auto space-y-3">
    <input disabled value="you@email.com" className="w-full p-3 border border-gray-300 rounded-lg shadow-sm text-sm bg-gray-50 text-gray-500" />
    <input disabled type="password" value="••••••••" className="w-full p-3 border border-gray-300 rounded-lg shadow-sm text-sm bg-gray-50 text-gray-500" />
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">I am a:</label>
      <div className="w-full p-3 border border-gray-300 rounded-lg shadow-sm text-sm bg-gray-50 text-gray-700 flex justify-between items-center">
        <span>{role === 'parent' ? 'Parent' : 'Babysitter'}</span>
        <span className="text-gray-400">▾</span>
      </div>
    </div>
    <div className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg text-center text-sm">Sign Up</div>
  </div>
);

/* ---------- parent demos ---------- */

const ParentCardDemo = ({ onComplete }) => {
  const [saved, setSaved] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 space-y-3">
      <h4 className="text-sm font-semibold text-gray-700">Payment Information</h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input disabled value="4242 4242 4242 4242" className="sm:col-span-2 px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-500" />
        <input disabled value="12/29 · 123" className="px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-500" />
      </div>
      {saved ? (
        <DemoSuccess>Card saved! You can now post jobs and accept applications.</DemoSuccess>
      ) : (
        <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setSaved(true); onComplete(); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
          Save Card
        </button>
      )}
    </div>
  );
};

const ParentPostJobDemo = ({ onComplete }) => {
  const [posted, setPosted] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 space-y-3">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title</label>
        <input disabled value="Date Night" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-700" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Date &amp; Time</label>
          <input disabled value="Sat, Jul 18 · 6–9 PM" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-700" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Pay Rate Per Hour ($)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <input disabled value="29.00" className="w-full pl-7 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-700" />
          </div>
        </div>
      </div>
      {posted ? (
        <DemoSuccess>Job posted! Every sitter gets an email notification right away.</DemoSuccess>
      ) : (
        <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setPosted(true); onComplete(); }} className={`w-full ${btn.blue}`}>
          Post Job
        </button>
      )}
    </div>
  );
};

const ParentAcceptDemo = ({ onComplete }) => {
  const [accepted, setAccepted] = useState(false);
  return (
    <AppCard>
      <StatusHeader
        dot={accepted ? 'bg-yellow-500 animate-pulse' : 'bg-blue-500 animate-pulse'}
        pill={accepted ? 'Awaiting Sitter Confirmation' : 'Pending'}
        pillClasses={accepted ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}
      />
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-300 to-amber-400 flex items-center justify-center text-white font-bold text-lg border-2 border-white shadow-sm">S</div>
          <div className="flex-1">
            <p className="text-lg font-semibold text-slate-800">Sarah M. <span className="text-yellow-500 text-sm font-normal">★ 4.9</span></p>
            <p className="text-xs text-slate-500">CPR certified · 5 yrs experience · 24 years old</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 italic bg-slate-50 rounded-lg p-3">"Hi! I'd love to watch your little ones — I'm free that whole evening."</p>
        {accepted ? (
          <DemoSuccess>Accepted! Sarah has 24 hours to confirm. Once she does, the job goes Active.</DemoSuccess>
        ) : (
          <div className="flex flex-wrap gap-3">
            <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setAccepted(true); onComplete(); }} className={btn.green}>
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <span>Accept</span>
              </span>
            </button>
            <button type="button" className={`${btn.red} opacity-60 cursor-default`}>
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                <span>Deny</span>
              </span>
            </button>
          </div>
        )}
      </div>
    </AppCard>
  );
};

const ParentPayDemo = ({ onComplete }) => {
  const [tipPct, setTipPct] = useState(0);
  const [paid, setPaid] = useState(false);
  const hours = 3.25; // sitter's actual logged hours
  const rate = 29;
  const base = hours * rate;
  const tip = base * (tipPct / 100);
  const platform = base * 0.10;
  // Break-even processing fee — mirrors src/utils/fees.js
  const processing = Math.ceil((0.0344 * base * 100 + 0.0315 * tip * 100 + 55) / 0.971) / 100;
  const total = base + platform + processing + tip;
  const Row = ({ label, value, bold }) => (
    <div className={`flex justify-between items-center px-3 py-2 bg-slate-50 rounded-lg text-sm ${bold ? 'font-bold text-slate-900' : 'text-slate-600'}`}>
      <span>{label}</span><span className={bold ? '' : 'font-medium text-slate-800'}>{value}</span>
    </div>
  );
  return (
    <AppCard>
      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 flex items-center space-x-3">
        <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-800">Confirm Payment</h3>
          <p className="text-xs text-slate-600">Complete your job payment</p>
        </div>
      </div>
      <div className="p-4 space-y-2">
        <Row label="Hours Worked:" value={hours.toFixed(2)} />
        <Row label="Hourly Rate:" value={`$${rate.toFixed(2)}`} />
        <Row label="Sitter Payment:" value={`$${base.toFixed(2)}`} />
        <Row label="Platform Fee (10%):" value={`$${platform.toFixed(2)}`} />
        <Row label="Processing Fee:" value={`$${processing.toFixed(2)}`} />
        {!paid && (
          <div className="flex gap-2 items-center py-1">
            <span className="text-xs text-slate-500">Add a tip:</span>
            {[0, 10, 15, 20].map((pct) => (
              <button key={pct} type="button" onClick={() => setTipPct(pct)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${tipPct === pct ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-300 text-slate-600'}`}>
                {pct === 0 ? 'None' : `${pct}%`}
              </button>
            ))}
          </div>
        )}
        {tip > 0 && <Row label="Tip (100% to sitter):" value={`$${tip.toFixed(2)}`} />}
        <Row bold label="Final Amount:" value={`$${total.toFixed(2)}`} />
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
          Card ending in …4242
        </div>
        {paid ? (
          <DemoSuccess>Payment complete! Sarah receives ${(base + tip).toFixed(2)} — parents cover all fees.</DemoSuccess>
        ) : (
          <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setPaid(true); onComplete(); }} className={`w-full ${btn.green}`}>
            Pay ${total.toFixed(2)}
          </button>
        )}
      </div>
    </AppCard>
  );
};

// Interactive lesson: why one range can't cross midnight, and how to fix it.
const ParentScheduleDemo = ({ onComplete }) => {
  const [split, setSplit] = useState(false);
  return (
    <AppCard>
      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h3 className="text-base font-bold text-slate-800">Select Dates &amp; Times</h3>
      </div>
      <div className="p-4 space-y-3">
        {/* The app's real info banners */}
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          There is a 3-hour minimum required for all time ranges.
        </p>
        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <strong>Overnight care?</strong> Split at midnight — end day one at 11:59 PM, start day two at 12:00 AM.
        </p>

        {/* Example 1 — regular daytime/evening care */}
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pt-1">Example 1 · Regular care — Saturday evening, 6–9 PM</p>
        <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2">
          <span className="text-sm font-medium text-slate-800">Sat, Jul 18</span>
          <div className="flex items-center gap-1.5">
            <TimeChip good>6:00 PM</TimeChip>
            <span className="text-slate-400">–</span>
            <TimeChip good>9:00 PM</TimeChip>
          </div>
        </div>
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          ✓ One date, one range, 3 hours — meets the minimum. Most jobs look like this.
        </p>

        {/* Example 2 — overnight care (interactive) */}
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pt-2">Example 2 · Overnight care — Friday 9 PM until Saturday 5 AM</p>
        <p className="text-xs text-slate-500">Watch what happens if you enter it as one range:</p>

        {!split ? (
          <>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50/50 px-3 py-2">
              <span className="text-sm font-medium text-slate-800">Fri, Jul 17</span>
              <div className="flex items-center gap-1.5">
                <TimeChip bad>9:00 PM</TimeChip>
                <span className="text-slate-400">–</span>
                <TimeChip bad>5:00 AM</TimeChip>
              </div>
            </div>
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              ⚠️ Start time must be before end time. For overnight care, split into two date entries.
            </div>
            <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setSplit(true); onComplete(); }} className={`w-full ${btn.amber}`}>
              Split it at midnight
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2">
              <span className="text-sm font-medium text-slate-800">Fri, Jul 17</span>
              <div className="flex items-center gap-1.5">
                <TimeChip good>9:00 PM</TimeChip>
                <span className="text-slate-400">–</span>
                <TimeChip good>11:59 PM</TimeChip>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2">
              <span className="text-sm font-medium text-slate-800">Sat, Jul 18</span>
              <div className="flex items-center gap-1.5">
                <TimeChip good>12:00 AM</TimeChip>
                <span className="text-slate-400">–</span>
                <TimeChip good>5:00 AM</TimeChip>
              </div>
            </div>
            <DemoSuccess>8 hours of overnight care, split correctly across two dates. The sitter adjusts the exact end time on her work log if she leaves early.</DemoSuccess>
          </>
        )}
      </div>
    </AppCard>
  );
};

/* ---------- sitter demos ---------- */

const SitterStripeDemo = ({ onComplete }) => {
  const [connected, setConnected] = useState(false);
  const items = [
    'Business type: Individual',
    'Personal details (name, DOB, address, last 4 of SSN)',
    'Industry: Education → Child Care · "Don’t have a website"',
    'Connect your bank account for payouts',
  ];
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 space-y-3">
      <h4 className="text-sm font-semibold text-gray-700">Payment Setup</h4>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
            {connected
              ? <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
              : <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border-2 border-gray-300" />}
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {connected ? (
        <DemoSuccess>Stripe connected! You're ready to receive payments.</DemoSuccess>
      ) : (
        <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setConnected(true); onComplete(); }} className={btn.stripe}>
          Connect Stripe Account
        </button>
      )}
    </div>
  );
};

const SitterApplyDemo = ({ onComplete }) => {
  const [state, setState] = useState('browse'); // browse | writing | applied
  return (
    <AppCard>
      <StatusHeader dot="bg-green-500 animate-pulse" pill="Open" pillClasses="bg-green-100 text-green-800" right="Posted today" />
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-slate-800">Date Night</h3>
            <p className="text-sm text-slate-600">Watching 3 little ones — kids will be bathed and fed.</p>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl font-semibold whitespace-nowrap">$29/hr</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Date</p>
              <p className="text-sm font-semibold text-slate-800">Sat, Jul 18 · 6–9 PM</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Location</p>
              <p className="text-sm font-semibold text-slate-800">McDonough, GA</p>
            </div>
          </div>
        </div>
        {state === 'browse' && (
          <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => setState('writing')} className={`w-full sm:w-auto ${btn.blue}`}>
            <span className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
              <span>Apply Now</span>
            </span>
          </button>
        )}
        {state === 'writing' && (
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Your Message</label>
            <div className="w-full p-4 border border-slate-300 rounded-xl text-sm text-slate-600 italic bg-white">
              "Hi! I'd love to watch your little ones — I'm free that whole evening."
            </div>
            <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setState('applied'); onComplete(); }} className={`w-full sm:w-auto ${btn.blue}`}>
              Submit Application
            </button>
          </div>
        )}
        {state === 'applied' && (
          <DemoSuccess>Application sent! The parent reviews it and can accept or deny.</DemoSuccess>
        )}
      </div>
    </AppCard>
  );
};

const SitterAcceptOfferDemo = ({ onComplete }) => {
  const [accepted, setAccepted] = useState(false);
  return (
    <AppCard>
      <StatusHeader
        dot={accepted ? 'bg-green-500 animate-pulse' : 'bg-yellow-500 animate-pulse'}
        pill={accepted ? 'Active' : 'Awaiting Sitter Confirmation'}
        pillClasses={accepted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
        right={!accepted && '⏰ 24h to respond'}
      />
      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Date Night</h3>
          <p className="text-sm text-slate-600">The parent accepted your application!</p>
        </div>
        {accepted ? (
          <DemoSuccess>Job confirmed! It's now Active — you can message the family anytime.</DemoSuccess>
        ) : (
          <div className="flex flex-wrap gap-3">
            <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setAccepted(true); onComplete(); }} className={btn.green}>
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                <span>Accept Offer</span>
              </span>
            </button>
            <button type="button" className={`${btn.red} opacity-60 cursor-default`}>Decline</button>
          </div>
        )}
      </div>
    </AppCard>
  );
};

const SitterWorkLogDemo = ({ onComplete }) => {
  const [stage, setStage] = useState('prefilled'); // prefilled | adjusted | submitted
  const adjusted = stage !== 'prefilled';
  return (
    <AppCard>
      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200/50 flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-800">Submit Work Logs</h3>
          <p className="text-xs text-slate-600">Track your work hours</p>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="font-medium text-slate-800">Sat, Jul 18</span>
          <div className="flex items-center gap-1.5">
            <TimeChip>6:00 PM</TimeChip>
            <span className="text-slate-400">–</span>
            <TimeChip good={adjusted}>{adjusted ? '9:15 PM' : '9:00 PM'}</TimeChip>
          </div>
        </div>
        <div className="p-3 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl flex items-center justify-between text-sm">
          <span className="font-semibold text-emerald-800">Total Hours</span>
          <span className="font-bold text-emerald-800">{adjusted ? '3.25 hrs · $94.25' : '3.00 hrs · $87.00'}</span>
        </div>
        <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <strong>Worked past midnight?</strong> One entry can't cross it — e.g. 7:00 PM – 1:30 AM becomes Jul 5: 7:00–11:59 PM <em>plus</em> Jul 6: 12:00 AM–1:30 AM.
        </p>
        {stage === 'prefilled' && (
          <>
            <p className="text-xs text-slate-500">Times pre-fill from the booking — but the parents got home at 9:15. Update your end time so you're paid for the real hours:</p>
            <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => setStage('adjusted')} className={`w-full ${btn.amber}`}>
              Adjust end time to 9:15 PM
            </button>
          </>
        )}
        {stage === 'adjusted' && (
          <button type="button" style={{ touchAction: 'manipulation' }} onClick={() => { setStage('submitted'); onComplete(); }} className={`w-full ${btn.blue}`}>
            <span className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              <span>Submit Work Logs</span>
            </span>
          </button>
        )}
        {stage === 'submitted' && (
          <DemoSuccess>Submitted! You can still edit it under "Pending Hours" until the parent pays.</DemoSuccess>
        )}
      </div>
    </AppCard>
  );
};

// Mirrors the "Payment Completed" box in the sitter's Job History
const SitterPaidDemo = () => (
  <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl">
    <div className="flex items-center space-x-3">
      <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>
      </div>
      <div>
        <p className="text-sm text-emerald-700 font-semibold">Payment Completed</p>
        <p className="text-lg font-bold text-emerald-800">$94.25 <span className="text-sm font-medium text-emerald-600">+ tip</span></p>
      </div>
    </div>
  </div>
);

const ReviewDemo = ({ onComplete, subject, title }) => {
  const [rating, setRating] = useState(0);
  return (
    <div className="bg-white rounded-lg shadow-lg p-5 max-w-sm mx-auto">
      <h3 className="text-xl font-bold mb-3 text-gray-900">{title}</h3>
      <div className="flex justify-center mb-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            onClick={() => { setRating(i); onComplete(); }}
            className={`text-3xl cursor-pointer transition-colors ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          >
            ★
          </span>
        ))}
      </div>
      <div className="w-full p-2 border rounded mb-3 text-sm text-gray-400">Leave a comment (optional)</div>
      {rating > 0
        ? <DemoSuccess>Thanks! Reviews build trust for everyone on the platform.</DemoSuccess>
        : <p className="text-center text-xs text-gray-500">Tap the stars to rate {subject}</p>}
    </div>
  );
};

// Stable component identities — inline arrows in the step config would get a
// new identity each render, causing React to remount the demo and drop its
// local state (the tapped star rating) mid-interaction.
const ParentSignupDemo = () => <SignupDemo role="parent" />;
const SitterSignupDemo = () => <SignupDemo role="sitter" />;
const ParentChatDemo = () => <ChatDemo fromOther={'"On my way — be there at 6!"'} fromMe={'"Perfect, the kids are excited! 😊"'} />;
const SitterChatDemo = () => <ChatDemo fromOther={'"See you at 6! The kids are excited 😊"'} fromMe={'"On my way!"'} />;
const ParentReviewDemo = (props) => <ReviewDemo {...props} title="Review Sitter" subject="your sitter" />;
const SitterReviewDemo = (props) => <ReviewDemo {...props} title="Review Parent" subject="the family" />;

/* ---------- step definitions ---------- */

const buildSteps = (role) => role === 'parent' ? [
  {
    title: 'Create your account',
    points: [
      'Sign up with your email and choose "Parent"',
      'Verify your email from the link we send you',
      'Add your address and your children’s info to your profile',
    ],
    demo: ParentSignupDemo,
    demoLabel: 'What you’ll see',
  },
  {
    title: 'Add a payment method',
    points: [
      'A card on file is required before you can post jobs',
      'Your card is stored securely by Stripe — never on our servers',
      'You’re only charged after a job is completed and you approve the hours',
    ],
    demo: ParentCardDemo,
    requireAction: true,
  },
  {
    title: 'Post a job',
    points: [
      'Give the job a title, description, and hourly rate ($22/hr minimum)',
      'Pick a start and end date — every day in between gets its own time ranges',
      'Multi-day jobs work great for recurring weekly care',
    ],
    demo: ParentPostJobDemo,
    requireAction: true,
  },
  {
    title: 'Dates, times & overnight care',
    points: [
      'Each selected day gets its own time ranges — add more than one per day if needed',
      'Every range must be at least 3 hours long',
      'A single range can’t cross midnight — overnight care is split across two dates',
    ],
    demo: ParentScheduleDemo,
    requireAction: true,
  },
  {
    title: 'Review applications',
    points: [
      'Sitters apply with a personal message — view their full profile, age, and certifications',
      'Find them under Your Jobs → Job Applications',
      'Accepting sends the sitter an offer they confirm within 24 hours',
    ],
    demo: ParentAcceptDemo,
    requireAction: true,
  },
  {
    title: 'Job day & messaging',
    points: [
      'Once the sitter confirms, the job is Active',
      'Message each other in the app — you both get email notifications',
      'Need to cancel? Free more than 24h before start; $25 fee inside 24h',
    ],
    demo: ParentChatDemo,
    demoLabel: 'What you’ll see',
  },
  {
    title: 'Approve hours & pay',
    points: [
      'After the job, the sitter submits their actual hours',
      'Review them under Your Jobs → Hours To Confirm',
      'If you don’t review within 24 hours, payment processes automatically',
    ],
    demo: ParentPayDemo,
    requireAction: true,
  },
  {
    title: 'Leave a review',
    points: ['Rate your sitter under Completed Jobs — it helps other families choose'],
    demo: ParentReviewDemo,
  },
] : [
  {
    title: 'Create your account',
    points: [
      'Sign up with your email and choose "Babysitter"',
      'Verify your email from the link we send you',
      'Add a clear photo, bio, date of birth, and your certifications (CPR, First Aid…)',
    ],
    demo: SitterSignupDemo,
    demoLabel: 'What you’ll see',
  },
  {
    title: 'Connect Stripe to get paid',
    points: [
      'Payments go straight to your bank through Stripe',
      'Go to Profile → Edit Profile → Payment Setup',
      'Later, view payouts anytime at connect.stripe.com/express_login',
    ],
    demo: SitterStripeDemo,
    requireAction: true,
  },
  {
    title: 'Find jobs & apply',
    points: [
      'Browse open jobs under Jobs List — you’ll also get an email when new ones post',
      'Apply with a short personal message to the family',
      'Apply to as many jobs as you like',
    ],
    demo: SitterApplyDemo,
    requireAction: true,
  },
  {
    title: 'Accept the offer',
    points: [
      'When a parent accepts your application, you get an offer under Your Jobs → Job Offers',
      'You have 24 hours to confirm before it expires',
      'You’ll need a card on file too — it covers the $25 fee if you cancel within 24h of start',
    ],
    demo: SitterAcceptOfferDemo,
    requireAction: true,
  },
  {
    title: 'Job day & messaging',
    points: [
      'Message the family in the app before and during the job',
      'Arrive on time — and enjoy!',
    ],
    demo: SitterChatDemo,
    demoLabel: 'What you’ll see',
  },
  {
    title: 'Submit your work log',
    points: [
      'After the job: Your Jobs → Active Jobs → Submit Work Log',
      'Times pre-fill from the booking — adjust them to your actual arrival and departure',
      'Worked past midnight? Split the entry: day one ends 11:59 PM, day two starts 12:00 AM',
      'You can edit it under Pending Hours until the parent pays',
    ],
    demo: SitterWorkLogDemo,
    requireAction: true,
  },
  {
    title: 'Get paid — 100% yours',
    points: [
      'You receive your full rate × hours, plus any tip — parents pay all fees',
      'Payment processes when the parent approves, or automatically after 24 hours',
      'Stripe deposits to your bank in 2–7 business days',
    ],
    demo: SitterPaidDemo,
    demoLabel: 'What you’ll see',
  },
  {
    title: 'Leave a review',
    points: ['Rate the family under Job History — it helps other sitters too'],
    demo: SitterReviewDemo,
  },
];

/* ---------- main component ---------- */

const Tutorial = ({ onClose, initialRole = null }) => {
  const [role, setRole] = useState(initialRole === 'parent' || initialRole === 'sitter' ? initialRole : null);
  const [stepIndex, setStepIndex] = useState(0);
  const [doneSteps, setDoneSteps] = useState({});
  const [finished, setFinished] = useState(false);

  const steps = role ? buildSteps(role) : [];
  const step = steps[stepIndex];
  const markDone = () => setDoneSteps((prev) => ({ ...prev, [`${role}-${stepIndex}`]: true }));
  const stepDone = doneSteps[`${role}-${stepIndex}`];
  const nextBlocked = step?.requireAction && !stepDone;

  const goNext = () => {
    if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
    else setFinished(true);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center overflow-y-auto py-4 px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl my-auto overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-sky-400 to-cyan-500 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <SparklesIcon className="w-6 h-6" />
            <h2 className="text-lg font-bold">
              {role ? `${role === 'parent' ? 'Parent' : 'Sitter'} walkthrough` : 'Interactive Tour'}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        {role && !finished && (
          <div className="h-1.5 bg-gray-100">
            <div
              className="h-full bg-gradient-to-r from-sky-400 to-cyan-500 transition-all duration-500"
              style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
            />
          </div>
        )}

        {/* Body */}
        <div className="p-6 sm:p-8">
          {!role && (
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome! Who are you?</h3>
              <p className="text-gray-500 mb-8">We'll walk you through the whole process, start to finish, with a hands-on demo.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setRole('parent')}
                  className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-sky-400 hover:bg-sky-50 transition-all duration-200 text-left"
                >
                  <UserGroupIcon className="w-10 h-10 text-sky-500 mb-3" />
                  <p className="font-bold text-gray-900">I'm a Parent</p>
                  <p className="text-sm text-gray-500 mt-1">Post jobs, choose a sitter, and pay securely</p>
                </button>
                <button
                  onClick={() => setRole('sitter')}
                  className="group p-6 rounded-2xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 transition-all duration-200 text-left"
                >
                  <UserIcon className="w-10 h-10 text-orange-500 mb-3" />
                  <p className="font-bold text-gray-900">I'm a Sitter</p>
                  <p className="text-sm text-gray-500 mt-1">Find jobs, get booked, and get paid — no fees</p>
                </button>
              </div>
            </div>
          )}

          {role && !finished && step && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-sky-500 mb-1">
                Step {stepIndex + 1} of {steps.length}
              </p>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
              <ul className="space-y-2">
                {step.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-400" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              {step.demo && (
                <DemoShell label={step.demoLabel}>
                  <step.demo onComplete={markDone} />
                </DemoShell>
              )}
            </div>
          )}

          {finished && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <CheckCircleIcon className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">You're all set! 🎉</h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                {role === 'parent'
                  ? 'Post your first job whenever you’re ready. The Help section below covers every detail.'
                  : 'Complete your profile and Stripe setup, then start applying. The Help section below covers every detail.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-sky-600 hover:to-cyan-600 transition-all duration-200 shadow-lg shadow-sky-500/25"
                >
                  Browse the Help Center
                </button>
                <button
                  onClick={() => { setRole(null); setStepIndex(0); setFinished(false); }}
                  className="px-6 py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Restart as {role === 'parent' ? 'Sitter' : 'Parent'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        {role && !finished && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <button
              onClick={() => (stepIndex === 0 ? setRole(null) : setStepIndex(stepIndex - 1))}
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-3">
              {nextBlocked && <span className="text-xs text-amber-600 font-medium hidden sm:inline">Try the demo above first ↑</span>}
              <button
                onClick={goNext}
                disabled={nextBlocked}
                style={{ touchAction: 'manipulation' }}
                className={`flex items-center gap-1.5 px-6 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${
                  nextBlocked
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/25 hover:from-sky-600 hover:to-cyan-600'
                }`}
              >
                {stepIndex === steps.length - 1 ? 'Finish' : 'Next'}
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tutorial;
